import { useMemo, type ComponentProps, type ReactNode } from "react";
import { PersistableView } from "@common/types/views/views";
import { GeneralLedgerView } from "@common/types/views/generalLedgerView";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import { AccountingFiltersSetter } from "../useAccountingTab";
import { AccountingReportFilters } from "../types";
import {
    applyAccountingReportBuiltInView,
    applyAccountingReportViewFilters,
} from "./accountingReportViewMapper";
import {
    accountingReportViewForComparison,
    buildAccountingReportBuiltInViews,
    buildAccountingReportPersistView,
    nextGeneralLedgerLayout,
} from "./accountingReportViewHelpers";
import { isAccountingReportBuiltInView } from "./accountingReportBuiltInViews";
import {
    AccountingReportViewId,
    getAccountingReportViewDefinition,
} from "./accountingReportViewRegistry";

export function AccountingReportViewToolbar({
    reportId,
    filters,
    setFilters,
    glLayout,
    viewConfig,
    children,
}: {
    reportId: AccountingReportViewId;
    filters: AccountingReportFilters;
    setFilters: AccountingFiltersSetter;
    glLayout?: {
        view: GeneralLedgerView;
        onViewChange: (view: GeneralLedgerView) => void;
        defaultView: GeneralLedgerView;
    };
    viewConfig?: ComponentProps<typeof ViewToolbar>["viewConfig"];
    children?: ReactNode;
}) {
    const { consolidationGroupId } = filters;
    const builtInViews = useMemo(
        () =>
            buildAccountingReportBuiltInViews({
                reportId,
                filters: { consolidationGroupId },
                defaultGlView: glLayout?.defaultView,
            }),
        [reportId, consolidationGroupId, glLayout?.defaultView],
    );

    const persistView = useMemo(
        () =>
            buildAccountingReportPersistView({
                reportId,
                filters,
                glView: glLayout?.view,
            }),
        [reportId, filters, glLayout?.view],
    );

    const handleViewSelect = (selected: PersistableView & { id?: string }) => {
        const applied = isAccountingReportBuiltInView(selected.id)
            ? applyAccountingReportBuiltInView({
                  reportId,
                  viewId: selected.id,
                  pageContext: filters,
              })
            : applyAccountingReportViewFilters({
                  reportId,
                  viewFilters: selected.filters,
              });
        setFilters((current) => ({ ...current, ...applied }));
        if (glLayout) {
            glLayout.onViewChange(
                nextGeneralLedgerLayout({
                    selected,
                    defaultView: glLayout.defaultView,
                }),
            );
        }
    };

    const handleViewChange = (changed: PersistableView) => {
        if (!glLayout) {
            return;
        }
        glLayout.onViewChange({
            ...glLayout.view,
            columns: nextGeneralLedgerLayout({
                selected: changed,
                defaultView: glLayout.defaultView,
            }).columns,
            options: changed.options ?? glLayout.view.options,
        });
    };

    return (
        <ViewToolbar<PersistableView>
            view={persistView}
            onViewChange={handleViewChange}
            onViewSelect={handleViewSelect}
            getViewForComparison={({ view, baseView }) =>
                accountingReportViewForComparison({
                    reportId,
                    filters,
                    persistView: view,
                    baseViewId: baseView?.id ?? null,
                })
            }
            viewConfig={viewConfig}
            builtInViews={builtInViews}
            defaultFilters={builtInViews[0].filters}
            baseViewCacheKey={
                getAccountingReportViewDefinition(reportId, filters).cacheKey
            }
        >
            {children}
        </ViewToolbar>
    );
}
